import {
  useState,
  memo,
  useCallback,
  useRef,
  useEffect,
  Key,
  ReactElement,
  ReactNode,
  useMemo
} from 'react'
import {
  Button,
  Checkbox,
  Flex,
  Input,
  InputRef,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  TreeSelect,
  Typography
} from 'antd'
import { CloseOutlined, FilterFilled, SearchOutlined } from '@ant-design/icons'
import debounce from 'lodash.debounce'
import { ColumnsType } from 'antd/es/table'
import { actionsApi } from 'api/actions'
import { productApi } from 'api/product'
import { sellerApi } from 'api/seller'
import noProductImage from 'assets/images/no-image-product.png'
import {
  EProductStatus,
  IParsedCategory,
  IProductQueryFilters,
  IProductResponse
} from 'models/product'
import { ISeller } from 'models/seller'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import { IMAGES_URL } from 'shared/constants/urls'
import { productTags } from 'shared/constants/product'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { highlightText } from 'shared/utils/highlightSearchText'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import styles from './goods/ProductForm/ProductForm.module.scss'

// Достаточно id: заранее отмеченные товары могут прийти не из списка товаров
type TSelectableProduct = Pick<IProductResponse, '_id'>

interface IGetProductModalProps<T extends TSelectableProduct> {
  multiple?: boolean
  visible: boolean
  onSave: (products: (IProductResponse | T)[]) => void | Promise<void>
  onClose: () => void
  title?: string
  saveText?: string
  // Текст подтверждения: с ним окно закрывается только крестиком и после
  // согласия, чтобы случайный клик мимо не сбросил отобранные товары
  closeConfirm?: string
  saveDisabled?: boolean
  children?: ReactNode
  // Товары, отмеченные при открытии окна
  initialSelected?: T[]
  // Подбор в фид: по умолчанию только товары, которые проходят условия общего фида
  feedFilter?: boolean
  // Товары, среди которых переворачивается выбор, — без учёта поиска и
  // фильтров. Без него инверсии нет
  loadInvertScope?: () => Promise<T[]>
}

enum ECategoryType {
  CATEGORY = 'category',
  SUB_CATEGORY = 'subCategory'
}

interface ISelectedCategory {
  id: string
  type: ECategoryType
}

export const GetProductModal = memo(
  <T extends TSelectableProduct = never>({
    onSave,
    visible,
    onClose,
    multiple = true,
    title = 'Товары',
    saveText = 'Добавить',
    closeConfirm,
    saveDisabled,
    children,
    initialSelected,
    feedFilter,
    loadInvertScope
  }: IGetProductModalProps<T>) => {
    const [globalSearch, setGlobalSearch] = useState('')
    const [selectedProducts, setSelectedProducts] = useState<
      Array<IProductResponse | T>
    >([])
    const [currentPage, setCurrentPage] = useState(1)
    const [total, setTotal] = useState(0)
    const pageSize = 7
    const [isLoading, setIsLoading] = useState(false)
    const [products, setProducts] = useState<IProductResponse[]>([])
    const [nameSearch, setNameSearch] = useState('')
    const nameSearchRef = useRef<InputRef>(null)
    const [categories, setCategories] = useState<IParsedCategory[]>([])
    const [categorySearch, setCategorySearch] = useState<string | undefined>(
      undefined
    )
    const [selectedCategory, setSelectedCategory] =
      useState<ISelectedCategory | null>(null)

    const [status, setStatus] = useState<EProductStatus | undefined>(undefined)
    const [sellers, setSellers] = useState<ISeller[]>([])
    const [selectedSellerId, setSelectedSellerId] = useState<
      string | undefined
    >(undefined)
    const [isSaving, setIsSaving] = useState(false)
    // Все товары под текущими фильтрами, со всех страниц: грузятся по первому
    // «Выбрать все» и сбрасываются при смене фильтров
    const [matchingProducts, setMatchingProducts] = useState<
      IProductResponse[] | null
    >(null)
    // Ответ на запрос всех товаров, пришедший после смены фильтров, устарел
    const filtersVersion = useRef(0)
    const [isInverted, setIsInverted] = useState(false)
    const [isInverting, setIsInverting] = useState(false)
    const [isFeedOnly, setIsFeedOnly] = useState(true)
    const [feedSellerIds, setFeedSellerIds] = useState<Set<string> | null>(null)
    const feedOnly = feedFilter && isFeedOnly

    const selectedIds = useMemo(
      () => selectedProducts.map((p) => p._id),
      [selectedProducts]
    )
    const isSelectAll = useMemo(() => {
      if (!matchingProducts?.length) return false
      const ids = new Set(selectedIds)
      return matchingProducts.every((p) => ids.has(p._id))
    }, [matchingProducts, selectedIds])

    useEffect(() => {
      if (visible) {
        const getCategories = async () => {
          try {
            const response = await productApi.getCategories({
              parsed: 'true'
            })
            setCategories(response.data as IParsedCategory[])
          } catch (e) {
            showServerError(e)
          }
        }
        const getSellers = async () => {
          try {
            const response = await sellerApi.getSearchSellers()
            setSellers(response.data)
          } catch (e) {
            showServerError(e)
          }
        }
        const getFeedSellers = async () => {
          try {
            const response = await actionsApi.getFeedSellerIds()
            setFeedSellerIds(new Set(response.data))
          } catch (e) {
            showServerError(e)
          }
        }
        setSelectedProducts(initialSelected ?? [])
        getCategories()
        getSellers()
        if (feedFilter) getFeedSellers()
        getProducts((currentPage - 1) * pageSize, status)
      }
    }, [visible])

    const resetMatchingProducts = () => {
      filtersVersion.current += 1
      setMatchingProducts(null)
    }

    const closeModal = () => {
      setSelectedProducts([])
      setSelectedSellerId(undefined)
      setIsFeedOnly(true)
      setIsInverted(false)
      resetMatchingProducts()
      onClose()
    }

    const getFilterQuery = (data?: IProductQueryFilters) => ({
      all_fields: data?.all_fields ?? globalSearch,
      name: data?.name ?? nameSearch,
      seller_id:
        data?.seller_id !== undefined ? data.seller_id : selectedSellerId,
      category_id:
        data?.category_id ?? selectedCategory?.type === ECategoryType.CATEGORY
          ? selectedCategory?.id
          : '',
      subCategory_id:
        data?.subCategory_id ??
        selectedCategory?.type === ECategoryType.SUB_CATEGORY
          ? selectedCategory?.id
          : '',
      feed_only: data?.feed_only ?? (feedOnly ? 'true' : '')
    })

    const loadMatchingProducts = async () => {
      if (matchingProducts) return matchingProducts
      const version = filtersVersion.current
      try {
        setIsLoading(true)
        const query = { ...getFilterQuery(), status, skip: '0' }
        let response = await productApi.getProducts({
          ...query,
          limit: String(total)
        })
        // total мог устареть, если ответ на новый поиск ещё не пришёл
        if (response.data.count > response.data.products.length) {
          response = await productApi.getProducts({
            ...query,
            limit: String(response.data.count)
          })
        }
        if (version !== filtersVersion.current) return null
        setMatchingProducts(response.data.products)
        return response.data.products
      } catch (e) {
        showServerError(e)
        return null
      } finally {
        setIsLoading(false)
      }
    }

    // Выбор вне найденных товаров не трогаем: он сохраняется при смене поиска
    const onChangeSelectAll = async (checked: boolean) => {
      const matching = await loadMatchingProducts()
      if (!matching) return
      const matchingIds = new Set(matching.map((p) => p._id))
      setSelectedProducts((prev) => {
        const rest = prev.filter((p) => !matchingIds.has(p._id))
        return checked ? [...rest, ...matching] : rest
      })
    }

    const invertSelection = async () => {
      if (!loadInvertScope) return
      try {
        setIsInverting(true)
        const scope = await loadInvertScope()
        setSelectedProducts((prev) => {
          const prevIds = new Set(prev.map((p) => p._id))
          return scope.filter((p) => !prevIds.has(p._id))
        })
        setIsInverted((prev) => !prev)
      } catch (e) {
        showServerError(e)
      } finally {
        setIsInverting(false)
      }
    }

    const chooseProduct = async () => {
      if (!selectedProducts.length) {
        return showMessage('info', 'Выберите продукт из таблицы', '')
      }
      try {
        setIsSaving(true)
        await onSave(selectedProducts)
        closeModal()
      } catch (e) {
        showServerError(e)
      } finally {
        setIsSaving(false)
      }
    }

    const getProducts = async (
      skip: number,
      status: EProductStatus | undefined,
      data?: IProductQueryFilters
    ) => {
      try {
        setIsLoading(true)
        const response = await productApi.getProducts({
          ...getFilterQuery(data),
          skip: String(skip),
          limit: String(pageSize),
          status
        })
        setTotal(response.data.count)
        setProducts(response.data.products)
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoading(false)
      }
    }

    const onChangeGlobalSearch = (searchValue: string) => {
      getProducts(0, status, { all_fields: searchValue })
      setCurrentPage(1)
      resetMatchingProducts()
    }

    const debouncedGetProductsByGlobalSearch = useCallback(
      debounce(onChangeGlobalSearch, DEBOUNCE_DELAY),
      [nameSearch, selectedCategory, status, selectedSellerId, feedOnly]
    )

    // Таблица отдаёт отмеченные строки только текущей страницы
    const onRowSelection = (_: Key[], selectedRows: IProductResponse[]) => {
      if (!multiple) return setSelectedProducts(selectedRows)
      const currentPageIds = new Set(products.map((p) => p._id))
      setSelectedProducts((prev) => [
        ...prev.filter((p) => !currentPageIds.has(p._id)),
        ...selectedRows
      ])
    }

    const onPageChange = (current: number) => {
      getProducts((current - 1) * pageSize, status)
      setCurrentPage(current)
    }

    const onSaveSearchName = (nameSearch: string) => {
      getProducts(0, status, { name: nameSearch })
      setCurrentPage(1)
      resetMatchingProducts()
    }

    const onSaveCategory = (selectedCategory: ISelectedCategory | null) => {
      resetMatchingProducts()
      if (!selectedCategory) {
        getProducts(0, status, { category_id: '', subCategory_id: '' })
        setSelectedCategory(null)
        return setCurrentPage(1)
      }
      if (selectedCategory.type === ECategoryType.CATEGORY) {
        getProducts(0, status, { category_id: selectedCategory.id })
        return setCurrentPage(1)
      }
      getProducts(0, status, { subCategory_id: selectedCategory.id })
      setCurrentPage(1)
    }

    const onChangeStatus = (newStatus: EProductStatus | undefined) => {
      getProducts(0, newStatus)
      setCurrentPage(1)
      resetMatchingProducts()
    }

    const onChangeFeedOnly = (checked: boolean) => {
      // Выбранного продавца может не быть среди продавцов фида
      const resetSeller =
        checked &&
        !!selectedSellerId &&
        !!feedSellerIds &&
        !feedSellerIds.has(selectedSellerId)
      if (resetSeller) setSelectedSellerId(undefined)
      setIsFeedOnly(checked)
      getProducts(0, status, {
        feed_only: checked ? 'true' : '',
        ...(resetSeller && { seller_id: '' })
      })
      setCurrentPage(1)
      resetMatchingProducts()
    }

    const sellerOptions = useMemo(
      () =>
        sellers
          .filter(
            (s) => !feedOnly || !feedSellerIds || feedSellerIds.has(s._id)
          )
          .map((s) => ({
            value: s._id,
            label: s.shop_details.shop_name
          })),
      [sellers, feedOnly, feedSellerIds]
    )

    const columns: ColumnsType<IProductResponse> = useMemo(
      () => [
        {
          title: 'Артикул',
          dataIndex: 'article',
          width: 101,
          render: (_, { article }) => highlightText(article ?? '', globalSearch)
        },
        {
          title: 'Фото',
          dataIndex: 'photo',
          width: 70,
          render: (_, { photo }) => {
            return photo && !!photo.length ? (
              <div
                style={{
                  width: 62,
                  height: 56,
                  backgroundImage: `url(${IMAGES_URL}${photo[0]})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            ) : (
              <img
                src={noProductImage}
                alt='Изображение товара'
                width={62}
                height={56}
              />
            )
          }
        },
        {
          title: 'Наименование товара',
          dataIndex: 'name',
          width: 310,
          render: (_, { name }) => highlightText(name ?? '', globalSearch),
          filterDropdown: ({ close }) => {
            return (
              <div
                className='filterWrapper'
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Input
                  ref={nameSearchRef}
                  value={nameSearch}
                  size='middle'
                  onChange={(e) => setNameSearch(e.target.value)}
                  onPressEnter={() => {
                    onSaveSearchName(nameSearch)
                    close()
                  }}
                  placeholder='Наименование товара'
                  className='filterInput'
                />
                <div className='filterFooterButtons'>
                  <Button
                    type='primary'
                    onClick={() => {
                      onSaveSearchName(nameSearch)
                      close()
                    }}
                    icon={<SearchOutlined />}
                    size='small'
                    className='filterButton'
                  >
                    Найти
                  </Button>
                  <Button
                    onClick={() => {
                      setNameSearch('')
                      onSaveSearchName('')
                      close()
                    }}
                    size='small'
                    className='filterButton'
                  >
                    Очистить
                  </Button>
                  <Button
                    type='link'
                    size='small'
                    onClick={() => {
                      close()
                    }}
                  >
                    Закрыть
                  </Button>
                </div>
              </div>
            )
          },
          onFilterDropdownOpenChange: (visible) => {
            if (visible) {
              setTimeout(() => nameSearchRef.current?.focus(), 0)
            }
          },
          filterIcon: () => (
            <SearchOutlined
              style={{
                color: nameSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
              }}
            />
          )
        },
        {
          title: 'Категория',
          dataIndex: 'category_id',
          width: 310,
          render: (_, { category_id, subCategory_id }) => {
            if (!category_id || !subCategory_id) {
              return (
                <Typography.Text style={{ opacity: 0.5 }}>
                  Не задана
                </Typography.Text>
              )
            }
            return (
              <>
                <Typography.Text>{category_id.name + ' >'}</Typography.Text>
                <br />
                <Typography.Text>{subCategory_id.name}</Typography.Text>
              </>
            )
          },
          filterDropdown: ({ close }) => {
            return (
              <div
                className='filterWrapper'
                onKeyDown={(e) => e.stopPropagation()}
              >
                <TreeSelect
                  value={categorySearch}
                  size='middle'
                  onChange={(value) => {
                    setCategorySearch(value)
                    setSelectedCategory(null)
                  }}
                  className='filterInput filterTreeSelect'
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  placeholder='Выберите категорию или подкатегорию'
                  onSelect={(_, item) => {
                    setSelectedCategory({
                      id: item.value,
                      type: item.type as ECategoryType
                    })
                  }}
                  treeData={categories}
                  showSearch
                  treeNodeFilterProp='title'
                  allowClear
                />
                <div className='filterFooterButtons'>
                  <Button
                    type='primary'
                    onClick={() => {
                      onSaveCategory(selectedCategory)
                      close()
                    }}
                    icon={<SearchOutlined />}
                    size='small'
                    className='filterButton'
                  >
                    Найти
                  </Button>
                  <Button
                    onClick={() => {
                      setCategorySearch(undefined)
                      onSaveCategory(null)
                      close()
                    }}
                    size='small'
                    className='filterButton'
                  >
                    Очистить
                  </Button>
                  <Button
                    type='link'
                    size='small'
                    onClick={() => {
                      close()
                    }}
                  >
                    Закрыть
                  </Button>
                </div>
              </div>
            )
          },
          filterIcon: () => (
            <FilterFilled
              style={{
                color: selectedCategory ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
              }}
            />
          )
        },
        {
          title: 'Цена',
          dataIndex: 'price',
          width: 101,
          render: (_, { price }) => {
            return (
              <Typography.Text style={{ opacity: price ? 1 : 0.5 }}>
                {price ? getNormalizedPrice(price) : 'Не задана'}
              </Typography.Text>
            )
          }
        },
        {
          title: 'Статус',
          dataIndex: 'status',
          width: 136,
          render: (_, { status }) => {
            return (
              <Tag color={productTags[status].color}>
                {productTags[status].label}
              </Tag>
            )
          },
          filterDropdown: ({ close }) => {
            return (
              <div
                className='filterWrapper'
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Radio.Group
                  onChange={(e) => {
                    setStatus(e.target.value)
                    onChangeStatus(e.target.value)
                    close()
                  }}
                  value={status}
                  style={{ display: 'block', marginBottom: 15 }}
                >
                  <Space direction='vertical' size={15}>
                    {Object.values(productTags).map((item) => (
                      <Radio key={item.value as string} value={item.value}>
                        <Tag color={item.color}>{item.label}</Tag>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
                <Button
                  onClick={() => {
                    setStatus(undefined)
                    onChangeStatus(undefined)
                    close()
                  }}
                  size='small'
                  className='filterButton'
                >
                  Очистить
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    close()
                  }}
                >
                  Закрыть
                </Button>
              </div>
            )
          },
          filterIcon: () => (
            <FilterFilled
              style={{
                color: status ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
              }}
            />
          )
        }
      ],
      [
        products,
        categories,
        nameSearch,
        categorySearch,
        selectedCategory,
        status,
        globalSearch
      ]
    )

    return (
      <Modal
        centered
        open={visible}
        footer={null}
        closable={false}
        onCancel={closeModal}
        maskClosable={!closeConfirm}
        keyboard={!closeConfirm}
        width={1100}
      >
        <Flex
          style={{
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 15
          }}
        >
          <Typography.Title level={4}>{title}</Typography.Title>
          {closeConfirm ? (
            <Popconfirm
              title={closeConfirm}
              okText='Закрыть'
              cancelText='Отмена'
              onConfirm={closeModal}
            >
              <CloseOutlined />
            </Popconfirm>
          ) : (
            <CloseOutlined onClick={closeModal} />
          )}
        </Flex>
        {children}
        {feedFilter && (
          <Checkbox
            checked={isFeedOnly}
            onChange={(e) => onChangeFeedOnly(e.target.checked)}
            style={{ marginBottom: 16 }}
          >
            Только товары, которые проходят условия общего фида
          </Checkbox>
        )}
        <Flex
          align='center'
          justify='space-between'
          gap={16}
          style={{ marginBottom: 32 }}
        >
          <Flex gap={12} align='center' style={{ flex: 1, minWidth: 0 }}>
            <Input
              value={globalSearch}
              onChange={(e) => {
                setGlobalSearch(e.target.value)
                debouncedGetProductsByGlobalSearch(e.target.value)
              }}
              placeholder='Поиск'
              size='large'
              suffix={<SearchOutlined />}
              className='searchInput'
              style={{ flex: 1 }}
            />
            <Select
              style={{ width: 220 }}
              placeholder='Фильтр по продавцу'
              size='large'
              showSearch
              allowClear
              optionFilterProp='label'
              value={selectedSellerId}
              onChange={(value) => {
                setSelectedSellerId(value)
                getProducts(0, status, { seller_id: value ?? '' })
                setCurrentPage(1)
                resetMatchingProducts()
              }}
              options={sellerOptions}
            />
            <Checkbox
              checked={isSelectAll}
              disabled={!total || isLoading}
              onChange={(e) => onChangeSelectAll(e.target.checked)}
            >
              Выбрать все товары
            </Checkbox>
            {multiple && loadInvertScope && (
              <Checkbox
                checked={isInverted}
                disabled={isInverting}
                onChange={invertSelection}
              >
                Инвертировать выбор
              </Checkbox>
            )}
          </Flex>
          <Flex align='center' gap={12} style={{ flexShrink: 0 }}>
            {selectedProducts.length > 0 && (
              <Typography.Text>
                Выбрано: {selectedProducts.length}
              </Typography.Text>
            )}
            {!closeConfirm && (
              <Button type='default' size='middle' onClick={closeModal}>
                Отменить
              </Button>
            )}
            <Button
              type='primary'
              size='middle'
              disabled={!selectedProducts.length || saveDisabled}
              loading={isSaving}
              onClick={chooseProduct}
            >
              {saveText}
            </Button>
          </Flex>
        </Flex>
        <Table
          scroll={{
            x: 900
          }}
          columns={columns}
          dataSource={products}
          loading={isLoading}
          rowKey={(el) => el._id}
          rowSelection={{
            type: multiple ? 'checkbox' : 'radio',
            selectedRowKeys: selectedIds,
            onChange: onRowSelection
          }}
          rowClassName='tableRow'
          footer={undefined}
          pagination={{
            current: currentPage,
            onChange: onPageChange,
            pageSize,
            total,
            showSizeChanger: false
          }}
        />
      </Modal>
    )
  }
) as <T extends TSelectableProduct = never>(
  props: IGetProductModalProps<T>
) => ReactElement
